import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SearchIcon, LayersIcon, SendIcon, CheckIcon, MailIcon } from 'lucide-react';
const tabs = [{
  id: 'search',
  icon: SearchIcon,
  title: 'Search Shopify stores & more',
  description: 'Find e-commerce leads across 14M+ active Shopify stores, WooCommerce sites, and 400+ platforms. Filter by category, rating, and tech stack — from Shopify apparel stores to BigCommerce electronics.'
}, {
  id: 'enrich',
  icon: LayersIcon,
  title: 'Enrich store data & contacts',
  description: 'Go beyond basic listings. Enrich any e-commerce store with 50+ data points — tech stack, avg. ticket size, revenue estimates, review ratings, social profiles, and verified owner emails.'
}, {
  id: 'sequence',
  icon: SendIcon,
  title: 'Launch email sequences',
  description: 'Stop exporting CSVs. Launch personalized, multi-step email sequences directly to your e-commerce leads — from the biggest Shopify stores to emerging DTC brands.'
}];
export function EcommerceWorkflow() {
  const [activeTab, setActiveTab] = useState(tabs[0].id);
  return <section className="py-24 bg-white" data-id="element-1244">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1245">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-1246">
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6 leading-tight" data-id="element-1247">
            The complete <span className="text-blue-600" data-id="element-1248">e-commerce store</span>{' '}
            workflow
          </h2>
          <p className="text-xl text-slate-600 leading-relaxed" data-id="element-1249">
            From discovering high-growth DTC brands to booking meetings —
            everything happens in one unified platform.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 items-center" data-id="element-1250">
          {/* Left: Tabs */}
          <div className="lg:col-span-5 space-y-2" data-id="element-1251">
            {tabs.map(tab => {
            const isActive = activeTab === tab.id;
            return <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`w-full text-left p-8 rounded-[2rem] transition-all duration-300 ${isActive ? 'bg-slate-50' : 'bg-white hover:bg-slate-50/50'}`} data-id="element-1252">
                  <div className="flex items-start gap-6" data-id="element-1253">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 transition-colors ${isActive ? 'bg-blue-100 text-blue-600' : 'bg-slate-100 text-slate-400'}`} data-id="element-1254">
                      <tab.icon className="w-6 h-6" data-id="element-1255" />
                    </div>
                    <div data-id="element-1256">
                      <h3 className={`text-xl font-bold mb-3 transition-colors ${isActive ? 'text-slate-900' : 'text-slate-700'}`} data-id="element-1257">
                        {tab.title}
                      </h3>
                      <p className={`text-base leading-relaxed transition-colors ${isActive ? 'text-slate-600' : 'text-slate-500'}`} data-id="element-1258">
                        {tab.description}
                      </p>
                    </div>
                  </div>
                </button>;
          })}
          </div>

          {/* Right: Dynamic Visuals */}
          <div className="lg:col-span-7" data-id="element-1259">
            <div className="bg-slate-50 rounded-[2.5rem] p-6 md:p-10 min-h-[450px] flex items-center justify-center relative overflow-hidden" data-id="element-1260">
              <AnimatePresence mode="wait" data-id="element-1261">
                {activeTab === 'search' && <motion.div key="search" initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -10
              }} transition={{
                duration: 0.2
              }} className="w-full max-w-md bg-white rounded-3xl p-8 shadow-sm border border-slate-100" data-id="element-1262">
                    <div className="flex items-center justify-between mb-6" data-id="element-1263">
                      <h4 className="font-bold text-slate-900" data-id="element-1264">
                        Store filters
                      </h4>
                      <span className="text-xs font-semibold text-blue-600 bg-blue-50 px-2.5 py-1 rounded-full" data-id="element-1265">
                        18,477 matches
                      </span>
                    </div>
                    <div className="space-y-5" data-id="element-1266">
                      <div data-id="element-1267">
                        <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2" data-id="element-1268">
                          Platform
                        </label>
                        <div className="w-full px-4 py-3 bg-slate-50 rounded-xl text-sm text-slate-700 border border-slate-100" data-id="element-1269">
                          Shopify
                        </div>
                      </div>
                      <div data-id="element-1270">
                        <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2" data-id="element-1271">
                          Category
                        </label>
                        <div className="w-full px-4 py-3 bg-slate-50 rounded-xl text-sm text-slate-700 border border-slate-100" data-id="element-1272">
                          Apparel
                        </div>
                      </div>
                      <div data-id="element-1273">
                        <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2" data-id="element-1274">
                          Rating
                        </label>
                        <div className="flex gap-2" data-id="element-1275">
                          {['Any', '3+', '3.5+', '4+', '4.5+'].map(r => <div key={r} className={`px-3 py-2 rounded-lg text-xs font-semibold border ${r === '4+' ? 'bg-blue-50 border-blue-200 text-blue-700' : 'bg-slate-50 border-slate-100 text-slate-500'}`} data-id="element-1276">
                              {r}
                            </div>)}
                        </div>
                      </div>
                      <div data-id="element-1277">
                        <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2" data-id="element-1278">
                          Reviews (count)
                        </label>
                        <div className="flex items-center gap-2" data-id="element-1279">
                          <div className="flex-1 px-4 py-3 bg-slate-50 rounded-xl text-sm text-slate-700 border border-slate-100 text-center" data-id="element-1280">
                            50
                          </div>
                          <span className="text-slate-400 text-xs" data-id="element-1281">–</span>
                          <div className="flex-1 px-4 py-3 bg-slate-50 rounded-xl text-sm text-slate-400 border border-slate-100 text-center" data-id="element-1282">
                            max
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>}

                {activeTab === 'enrich' && <motion.div key="enrich" initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -10
              }} transition={{
                duration: 0.2
              }} className="w-full max-w-xl" data-id="element-1283">
                    <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden" data-id="element-1284">
                      <div className="bg-slate-50 px-5 py-3 border-b border-slate-100 flex justify-between items-center" data-id="element-1285">
                        <span className="text-xs font-bold text-slate-500 uppercase tracking-wider" data-id="element-1286">
                          Enriched store profile
                        </span>
                        <span className="text-xs font-semibold text-green-600 bg-green-50 px-2 py-0.5 rounded-full" data-id="element-1287">
                          54 data points
                        </span>
                      </div>
                      <div className="px-5 pt-4 pb-2 flex items-center gap-3 border-b border-slate-100" data-id="element-1288">
                        <div className="w-9 h-9 rounded-lg bg-blue-50 border border-blue-100 flex items-center justify-center text-sm font-bold text-blue-600" data-id="element-1289">
                          K
                        </div>
                        <div data-id="element-1290">
                          <div className="text-sm font-bold text-slate-900" data-id="element-1291">
                            kith.com
                          </div>
                          <div className="text-[11px] text-slate-400" data-id="element-1292">
                            Shopify Plus · Apparel
                          </div>
                        </div>
                      </div>
                      <div className="divide-y divide-slate-100" data-id="element-1293">
                        {[{
                      label: 'Platform',
                      value: 'Shopify Plus'
                    }, {
                      label: 'Number of locations',
                      value: '3'
                    }, {
                      label: 'Avg. ticket size',
                      value: '$185'
                    }, {
                      label: 'Est. revenue',
                      value: '$50M+'
                    }, {
                      label: 'Tech stack',
                      value: 'Klaviyo, GA, Facebook Pixel, Affirm'
                    }, {
                      label: 'Social profiles',
                      value: 'Instagram, Twitter, TikTok'
                    }, {
                      label: 'Owner contact',
                      value: 'Ronnie Fieg · ronnie@kith.com',
                      verified: true
                    }].map((field, i) => <div key={i} className="px-5 py-3 flex items-center justify-between" data-id="element-1294">
                            <span className="text-xs font-medium text-slate-500" data-id="element-1295">
                              {field.label}
                            </span>
                            <span className={`text-xs font-semibold ${field.verified ? 'text-green-700 flex items-center gap-1' : 'text-slate-800'}`} data-id="element-1296">
                              {field.verified && <CheckIcon className="w-3 h-3" strokeWidth={3} data-id="element-1297" />}
                              {field.value}
                            </span>
                          </div>)}
                      </div>
                    </div>
                  </motion.div>}

                {activeTab === 'sequence' && <motion.div key="sequence" initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -10
              }} transition={{
                duration: 0.2
              }} className="w-full max-w-md" data-id="element-1298">
                    <div className="bg-white rounded-3xl p-6 shadow-sm border border-slate-100" data-id="element-1299">
                      <div className="flex items-center gap-3 mb-6 pb-4 border-b border-slate-100" data-id="element-1300">
                        <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center" data-id="element-1301">
                          <SendIcon className="w-5 h-5 text-blue-600" data-id="element-1302" />
                        </div>
                        <div data-id="element-1303">
                          <h4 className="font-bold text-slate-900" data-id="element-1304">
                            Shopify Apparel Campaign
                          </h4>
                          <p className="text-xs text-slate-500" data-id="element-1305">
                            Active • 1,200 prospects
                          </p>
                        </div>
                      </div>

                      <div className="space-y-4 relative before:absolute before:inset-0 before:ml-5 before:-translate-x-px md:before:mx-auto md:before:translate-x-0 before:h-full before:w-0.5 before:bg-gradient-to-b before:from-transparent before:via-slate-200 before:to-transparent" data-id="element-1306">
                        <div className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active" data-id="element-1307">
                          <div className="flex items-center justify-center w-10 h-10 rounded-full border-4 border-white bg-blue-100 text-blue-600 shadow shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 z-10" data-id="element-1308">
                            <MailIcon className="w-4 h-4" data-id="element-1309" />
                          </div>
                          <div className="w-[calc(100%-4rem)] md:w-[calc(50%-2.5rem)] bg-slate-50 p-3 rounded-xl border border-slate-100" data-id="element-1310">
                            <div className="text-xs font-bold text-slate-900 mb-1" data-id="element-1311">
                              Step 1: Initial email
                            </div>
                            <div className="text-[10px] text-slate-500" data-id="element-1312">
                              Sent immediately
                            </div>
                          </div>
                        </div>

                        <div className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active" data-id="element-1313">
                          <div className="flex items-center justify-center w-10 h-10 rounded-full border-4 border-white bg-slate-100 text-slate-400 shadow shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 z-10" data-id="element-1314">
                            <MailIcon className="w-4 h-4" data-id="element-1315" />
                          </div>
                          <div className="w-[calc(100%-4rem)] md:w-[calc(50%-2.5rem)] bg-white p-3 rounded-xl border border-slate-100" data-id="element-1316">
                            <div className="text-xs font-bold text-slate-900 mb-1" data-id="element-1317">
                              Step 2: Follow up
                            </div>
                            <div className="text-[10px] text-slate-500" data-id="element-1318">
                              Wait 3 days
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
    </section>;
}